/**
 * "Add a derived field" — a field Wend computes from another one.
 *
 * ## ANY SOURCE, NOT A FIXED ONE
 *
 * The owner's brief: "it's not only about dob/date_of_birth, it can be any
 * custom column". So the source is a picker over EVERY field whose type the
 * kind reads (`DERIVED_FIELD_SOURCE_TYPE`): Age (`years_since`) can come from
 * `dob`, `date_of_birth`, `birthday`, "Joining date", "Policy start" — whatever
 * date column this workspace has, and its label is whatever the person types.
 * Bands read any number field.
 *
 * ## SUGGESTIONS ARE OFFERED, NEVER APPLIED
 *
 * The chips across the top are "Age from <column>" for each date column whose
 * name reads like a birth date (`looksLikeBirthDate`: dob, birth, born) and
 * that nothing derives an age from yet. They come from the catalogue's own
 * `suggestions`, with the same test run here over the catalogue as a fallback,
 * so a suggestion never depends on which side computed it. One click fills the
 * form; the person still presses Add. Nothing is created behind their back —
 * a column called "Born in (city)" is a birthplace, and only a person can tell.
 *
 * ## A REAL DIALOG
 *
 * `role="dialog"` with `aria-modal`, labelled by its heading, the first control
 * focused on open, Escape and the backdrop close it, and the caller returns
 * focus to the button that opened it. Bands are typed as "18-24, 25-34" and
 * parsed against `numberBandsSchema`, so overlapping or reversed bands are
 * refused here with the contract's own sentence.
 */
import {
  DERIVED_FIELD_KIND_LABEL,
  DERIVED_FIELD_KIND_VALUES,
  DERIVED_FIELD_SOURCE_TYPE,
  EXTRAS_FIELD_PREFIX,
  createDerivedFieldRequestSchema,
  looksLikeBirthDate,
  numberBandsSchema,
  slugifyFieldLabel,
  type CreateDerivedFieldRequest,
  type DerivedFieldKind,
  type DerivedFieldSuggestion,
  type Field,
} from '@wend/shared';
import { Button, FormField, Input, cn, focusRing } from '@wend/ui';
import { Sparkles, X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';

import { RULE_SELECT_CLASS } from '../../../components/rules';
import { errorMessage } from '../../../lib/api/errors';

export interface DerivedFieldDialogProps {
  readonly fields: readonly Field[];
  readonly suggestions: readonly DerivedFieldSuggestion[];
  readonly onClose: () => void;
  readonly onCreate: (request: CreateDerivedFieldRequest) => Promise<void>;
  /** Prefill from a suggestion chip pressed outside the dialog. */
  readonly initial?: DerivedFieldSuggestion | null;
}

/**
 * Every "Age from <column>" worth offering: the catalogue's suggestions, plus
 * any date column it did not mention whose name reads like a birth date and
 * that no derived field already reads.
 */
export function ageSuggestions(fields: readonly Field[], suggestions: readonly DerivedFieldSuggestion[]): DerivedFieldSuggestion[] {
  const out = [...suggestions];
  const derivedSources = new Set(
    fields.flatMap((field) => (field.origin.kind === 'derived' && field.origin.derivedKind === 'years_since' ? [field.origin.sourceField] : [])),
  );
  for (const field of fields) {
    if (field.origin.kind !== 'column' || field.type !== 'date') continue;
    if (!looksLikeBirthDate(field.origin.column)) continue;
    if (derivedSources.has(field.key) || out.some((suggestion) => suggestion.sourceField === field.key)) continue;
    out.push({
      kind: 'years_since',
      sourceField: field.key,
      label: 'Age',
      key: slugifyFieldLabel(`age ${field.origin.column}`),
      reason: `“${field.origin.column}” reads like a birth date.`,
    });
  }
  return out;
}

/** "18-24, 25-34, 35+" is not accepted; "18-24, 25-34, 35-120" is. */
export function parseBands(text: string): [number, number][] | null {
  const parts = text
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '');
  const bands: [number, number][] = [];
  for (const part of parts) {
    const match = /^(-?\d+(?:\.\d+)?)\s*[-–—]\s*(-?\d+(?:\.\d+)?)$/.exec(part);
    if (match === null) return null;
    bands.push([Number(match[1]), Number(match[2])]);
  }
  return bands.length === 0 ? null : bands;
}

function sourceLabel(fields: readonly Field[], key: string): string {
  const field = fields.find((candidate) => candidate.key === key);
  if (field !== undefined) return field.label;
  return key.startsWith(EXTRAS_FIELD_PREFIX) ? key.slice(EXTRAS_FIELD_PREFIX.length) : key;
}

const KIND_WORD: Readonly<Record<DerivedFieldKind, string>> = {
  years_since: 'Years since',
  days_since: 'Days since',
  month_of: 'Month of',
  weekday_of: 'Weekday of',
  number_band: 'Band of',
};

export function DerivedFieldDialog({ fields, suggestions, onClose, onCreate, initial = null }: DerivedFieldDialogProps) {
  const headingId = useId();
  const kindId = useId();
  const sourceId = useId();
  const firstRef = useRef<HTMLSelectElement>(null);

  const [kind, setKind] = useState<DerivedFieldKind>(initial?.kind ?? 'years_since');
  const [source, setSource] = useState<string>(initial?.sourceField ?? '');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [labelTouched, setLabelTouched] = useState(initial !== null);
  const [bands, setBands] = useState('18-24, 25-34, 35-44, 45-54, 55-64, 65-120');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    firstRef.current?.focus();
  }, []);

  const sources = fields.filter((field) => !field.hidden && field.type === DERIVED_FIELD_SOURCE_TYPE[kind]);
  const chips = ageSuggestions(fields, suggestions);

  function suggestLabel(nextKind: DerivedFieldKind, nextSource: string): string {
    const column = sourceLabel(fields, nextSource);
    if (nextKind === 'years_since' && looksLikeBirthDate(column)) return 'Age';
    return nextSource === '' ? '' : `${KIND_WORD[nextKind]} ${column}`;
  }

  function pick(nextKind: DerivedFieldKind, nextSource: string): void {
    setKind(nextKind);
    setSource(nextSource);
    if (!labelTouched) setLabel(suggestLabel(nextKind, nextSource));
    setError(null);
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (pending) return;
    const parsedBands = kind === 'number_band' ? parseBands(bands) : null;
    if (kind === 'number_band') {
      const checked = numberBandsSchema.safeParse(parsedBands ?? []);
      if (parsedBands === null || !checked.success) {
        setError(
          parsedBands === null
            ? 'Write the bands as low-high pairs separated by commas, like 18-24, 25-34.'
            : (checked.error?.issues[0]?.message ?? 'Those bands are not valid.'),
        );
        return;
      }
    }
    const request = {
      kind,
      label: label.trim(),
      sourceField: source,
      params: kind === 'number_band' ? { bands: parsedBands } : {},
    };
    const parsed = createDerivedFieldRequestSchema.safeParse(request);
    if (!parsed.success || source === '') {
      setError(source === '' ? 'Choose the field it is worked out from.' : label.trim() === '' ? 'Give it a name.' : (parsed.error?.issues[0]?.message ?? 'Check the form.'));
      return;
    }
    setPending(true);
    setError(null);
    try {
      await onCreate(parsed.data);
    } catch (caught) {
      setError(errorMessage(caught));
      setPending(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-brand-b/40 bg-surface-1 p-5 shadow-[0_24px_60px_-16px_rgba(0,0,0,0.85)]"
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            onClose();
            return;
          }
          // aria-modal promises the page behind is out of reach, so Tab wraps
          // inside the dialog rather than walking out into the page under it.
          if (event.key === 'Tab') {
            const focusable = [
              ...event.currentTarget.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), select:not([disabled])'),
            ];
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (first === undefined || last === undefined) return;
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first.focus();
            }
          }
        }}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 id={headingId} className="text-[1.05rem] font-semibold text-text">
              Add a derived field
            </h2>
            <p className="mt-1 text-[0.84rem] text-muted">Wend works it out for every person from another field, and keeps it current.</p>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className={cn('grid size-8 place-items-center rounded-sm text-muted hover:text-text', focusRing)}>
            <X aria-hidden="true" className="size-4" />
          </button>
        </div>

        {chips.length > 0 && (
          <div className="mb-4">
            <p className="mb-1.5 text-[0.76rem] text-muted">Suggested from your columns</p>
            <div className="flex flex-wrap gap-1.5">
              {chips.map((chip) => (
                <button
                  key={`${chip.kind}:${chip.sourceField}`}
                  type="button"
                  data-role="age-suggestion"
                  onClick={() => {
                    setKind(chip.kind);
                    setSource(chip.sourceField);
                    setLabel(chip.label);
                    setLabelTouched(true);
                    setError(null);
                  }}
                  className={cn(
                    'inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-brand-b/40 bg-brand-a/16 px-3 py-1 text-[0.8rem] text-brand-ink-strong hover:bg-brand-a/26',
                    focusRing,
                  )}
                >
                  <Sparkles aria-hidden="true" className="size-3.5" />
                  {`${chip.label} from ${sourceLabel(fields, chip.sourceField)}`}
                </button>
              ))}
            </div>
          </div>
        )}

        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            void onSubmit(event);
          }}
        >
          <div className="flex flex-col gap-1.5">
            <label htmlFor={kindId} className="text-[0.86rem] font-medium text-text">
              What to work out
            </label>
            <select
              id={kindId}
              ref={firstRef}
              className={RULE_SELECT_CLASS}
              value={kind}
              disabled={pending}
              onChange={(event) => {
                const nextKind = event.target.value as DerivedFieldKind;
                const stillFits = fields.some((field) => field.key === source && field.type === DERIVED_FIELD_SOURCE_TYPE[nextKind]);
                pick(nextKind, stillFits ? source : '');
              }}
            >
              {DERIVED_FIELD_KIND_VALUES.map((value) => (
                <option key={value} value={value}>
                  {DERIVED_FIELD_KIND_LABEL[value]}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor={sourceId} className="text-[0.86rem] font-medium text-text">
              From which field
            </label>
            <select
              id={sourceId}
              className={RULE_SELECT_CLASS}
              value={source}
              disabled={pending}
              onChange={(event) => pick(kind, event.target.value)}
            >
              <option value="">{sources.length === 0 ? `No ${DERIVED_FIELD_SOURCE_TYPE[kind]} fields yet` : 'Choose a field…'}</option>
              {sources.map((field) => (
                <option key={field.key} value={field.key} disabled={field.needsDateFormat}>
                  {field.needsDateFormat ? `${field.label} — set its date format first` : field.label}
                </option>
              ))}
            </select>
            <p className="text-[0.76rem] text-muted">
              Any {DERIVED_FIELD_SOURCE_TYPE[kind]} field works — a birth date, a joining date, a policy start, whatever your uploads call it.
            </p>
          </div>

          <FormField label="Name" required>
            <Input
              value={label}
              onChange={(event) => {
                setLabel(event.target.value);
                setLabelTouched(true);
              }}
              placeholder="Age"
              disabled={pending}
              autoComplete="off"
            />
          </FormField>

          {kind === 'number_band' && (
            <FormField label="Bands" description="Low-high pairs, in order, not overlapping. A value outside every band stays unknown.">
              <Input value={bands} onChange={(event) => setBands(event.target.value)} disabled={pending} autoComplete="off" />
            </FormField>
          )}

          {error !== null && (
            <p role="alert" className="text-[0.84rem] text-bad">
              {error}
            </p>
          )}

          <div className="flex flex-wrap gap-3">
            <Button type="submit" loading={pending}>
              {pending ? 'Adding the field' : 'Add field'}
            </Button>
            <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
